import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton, Pressable, Skeleton, Text } from '@fluent/ui';
import { useMissions, missionsByTier, isDone, type MissionsState } from './hooks.js';
import { CoinGlyph } from '../profile/icons.js';
import './MissionsCard.css';

/** Collapsed preference survives reloads — per device, like the theme. */
const COLLAPSE_KEY = 'fluent.home.missions.collapsed';

/**
 * MissionsCard — the daily-missions door at the top of Home.
 * Compact: today's missions as progress pips + the coin balance chip.
 * The whole card routes to /missions. A chevron toggle collapses it to a
 * slim head row (Tornike 2026-07-20 — "so it takes less space"); it also
 * auto-slims into a "done!" row once all daily missions are complete.
 */
export function MissionsCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useMissions();

  if (isLoading && !data) {
    // Skeleton mirrors the loaded card (head + 4 daily rows) so the skill
    // tree below doesn't jump when data lands.
    return (
      <div className="fluent-missions-card" aria-busy="true">
        <div className="fluent-missions-card__head">
          <Skeleton width="40%" height={20} />
          <Skeleton width={64} height={24} />
        </div>
        <div className="fluent-missions-card__list">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} width={i % 2 ? '70%' : '85%'} height={20} />
          ))}
        </div>
      </div>
    );
  }
  if (isError && !data) {
    // Slim honest retry row — vanishing entirely would yank the tree up.
    return (
      <div className="fluent-missions-card" data-error="">
        <div className="fluent-missions-card__head">
          <Text weight="bold" size="sm" tone="muted">
            {t('missions.error_body')}
          </Text>
          <Button variant="ghost" size="sm" onClick={() => void refetch()}>
            {t('missions.retry')}
          </Button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  return <MissionsCardView state={data} onOpen={() => navigate('/achievements')} />;
}

export function MissionsCardView({ state, onOpen }: { state: MissionsState; onOpen: () => void }) {
  const { t } = useTranslation();
  const daily = missionsByTier(state, 'daily');
  const allDone = daily.length > 0 && daily.every(isDone);
  const doneCount = daily.filter(isDone).length;
  const [collapsed, setCollapsed] = useState<boolean>(
    () => typeof window !== 'undefined' && window.localStorage.getItem(COLLAPSE_KEY) === '1',
  );
  const onToggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        // Private mode — the toggle still works for this visit.
      }
      return next;
    });
  };

  return (
    <div className="fluent-missions-card__wrap">
      <Pressable
        className="fluent-missions-card"
        data-done={allDone || undefined}
        data-collapsed={collapsed || undefined}
        onClick={onOpen}
      >
        <div className="fluent-missions-card__head">
          <Text weight="bold" size="lg">
            {allDone ? t('missions.card_done') : t('missions.card_title')}
          </Text>
          {collapsed && !allDone && (
            <span className="fluent-missions-card__summary">
              {doneCount}/{daily.length}
            </span>
          )}
          <span className="fluent-missions-card__coins">
            <Icon size={16}>
              <CoinGlyph />
            </Icon>
            {state.balance}
            <span className="fluent-sr-only">{t('missions.balance_label')}</span>
          </span>
        </div>
        {!allDone && !collapsed && (
          <ul className="fluent-missions-card__list">
            {daily.map((m) => (
              <li key={m.id} className="fluent-missions-card__item" data-done={isDone(m) || undefined}>
                <span className="fluent-missions-card__check" aria-hidden="true">
                  {isDone(m) ? '✓' : ''}
                </span>
                <span className="fluent-missions-card__title">{t(`missions.m.${m.id}.title`)}</span>
                <span className="fluent-missions-card__progress">
                  {Math.min(m.progress, m.target)}/{m.target}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Pressable>
      {!allDone && (
        <IconButton
          className="fluent-missions-card__toggle"
          label={collapsed ? t('missions.expand_label') : t('missions.collapse_label')}
          onPress={onToggle}
          aria-expanded={!collapsed}
        >
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </IconButton>
      )}
    </div>
  );
}
